import React, { useState } from "react";
import { Grid, Typography } from "@mui/material";
import theme from "../../theme";
import { Field, Form, Formik } from "formik";
import { Inputs } from "../formInputs";
import { Base } from "../base";
import * as Icons from "@mui/icons-material";
import { HexColorPicker } from "react-colorful";
import JobStatus from "../../types/jobStatus";
import * as Yup from "yup";
import IconSwitcher, { FilledIconName } from "../base/IconSwitcher";
import { useSelector } from "../../store";
import { closestCenter, DndContext, DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import StatusSortableItem from "../views/JobType/StatusSortableItem";

interface Props {
  values: JobStatus[];
  handleAdd: (values: JobStatus) => void;
  handleCancel: () => void;
  renderOption: (
    props: React.HtmlHTMLAttributes<HTMLLIElement>,
    option: any
  ) => JSX.Element;
  handleRemove: (value: JobStatus) => void;
  move: (num1: number, num2: number) => void;
}

const ManageJobTypeStatusForm: React.FC<Props> = (props) => {
  const { values, handleAdd, handleCancel, renderOption, handleRemove, move } =
    props;
  const [color, setColor] = useState("#000000");
  const [deleteMode, setDeleteMode] = useState<JobStatus | undefined>(
    undefined
  );
  const [order, setOrder] = useState<number>(values.length + 1);

  const excluded = ["Outlined", "Rounded", "Sharp", "TwoTone"];
  const { jobType } = useSelector((store) => store.jobType);

  const initialValues: JobStatus = {
    id: 0,
    iconColor: "",
    jobTypeId: jobType?.id,
    icon: "",
    name: "",
    order: order,
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Required").typeError("Required"),
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, { resetForm }) => {
        handleAdd({ ...values, order });
        resetForm();
        setOrder(order + 1);
      }}
    >
      {({ setFieldValue, resetForm, values: statusValues }) => (
        <Form noValidate>
          <Grid
            container
            flex={1}
            sx={{
              width: { lg: 1024, md: 900, xs: window.innerWidth },
              backgroundColor: theme.palette.background.paper,
              paddingX: 5,
              paddingY: 3,
            }}
            spacing={2}
          >
            {deleteMode === undefined ? (
              <>
                <Grid size={9}>
                  <Grid container spacing={1}>
                    <Grid size={6}>
                      <Field
                        name="name"
                        label="Status/Type Name"
                        component={Inputs.Text}
                        required
                      />
                    </Grid>
                    <Grid size={6}>
                      <Field
                        component={Inputs.Dropdown}
                        name="icon"
                        label="Icon (Optional)"
                        options={Object.keys(Icons)
                          .filter((icon) => {
                            return !excluded.some((suffix) =>
                              icon.endsWith(suffix)
                            );
                          })
                          .map((iconName) => ({
                            key: iconName,
                            value: iconName,
                          }))}
                        staticFilter
                        onSelected={(e: any, v: any) => {
                          setFieldValue("icon", v.value);
                        }}
                        renderOption={renderOption}
                      />
                    </Grid>
                  </Grid>
                  <Grid>
                    <Typography variant="caption">Color</Typography>
                    <HexColorPicker
                      color={color}
                      onChange={(c) => {
                        setColor(c);
                        setFieldValue("iconColor", c);
                      }}
                      style={{ width: "100%" }}
                    />
                  </Grid>
                  <Grid container mt={2} justifyContent={"space-between"}>
                    <Base.Button
                      text="Close"
                      type="secondary"
                      onClick={() => {
                        handleCancel();
                        resetForm();
                      }}
                    />
                    <Inputs.Submit
                      isSubmitting={false}
                      text={statusValues.id > 0 ? "Save" : "Add"}
                    />
                  </Grid>
                </Grid>
                <Grid size={3}>
                  <DndContext
                    onDragEnd={(e: DragEndEvent) => {
                      const { over, active } = e;

                      if (!over) return;

                      const oldIndex = values.indexOf(
                        values.find((s) => s.order === active.id)!
                      );
                      const newIndex = values.indexOf(
                        values.find((s) => s.order === over.id)!
                      );

                      move(oldIndex, newIndex);
                    }}
                    collisionDetection={closestCenter}
                  >
                    <ul style={{ width: "100%", margin: 0, padding: 0 }}>
                      <Grid
                        container
                        flex={1}
                        alignContent={"flex-start"}
                        sx={{ height: "350px", overflow: "auto" }}
                      >
                        <SortableContext
                          items={values.map((v) => v.order)}
                          strategy={verticalListSortingStrategy}
                        >
                          {values.map((s, i) => (
                            <StatusSortableItem status={s} key={i}>
                              <Grid
                                container
                                direction={"row"}
                                alignItems={"center"}
                                justifyContent={"space-between"}
                                gap={1}
                                key={s.id > 0 ? s.id : s.name}
                                flex={1}
                              >
                                <Grid container>
                                  {s.name}{" "}
                                  {s.icon && (
                                    <IconSwitcher
                                      name={s.icon as FilledIconName}
                                      style={{
                                        fontSize: 25,
                                        color: s.iconColor ?? "black",
                                      }}
                                    />
                                  )}
                                </Grid>
                                <Grid>
                                  {s.id > 0 && (
                                    <Icons.Edit
                                      onClick={() => {
                                        setFieldValue("id", s.id);
                                        setFieldValue("name", s.name);
                                        if (!!s.icon) {
                                          setFieldValue(
                                            "icon",
                                            Object.keys(Icons)
                                              .filter((icon) => {
                                                return !excluded.some(
                                                  (suffix) =>
                                                    icon.endsWith(suffix)
                                                );
                                              })
                                              .filter(
                                                (iconName) =>
                                                  iconName === s.icon
                                              )
                                              .map((i) => i)[0]
                                          );
                                        }
                                        if (!!s.iconColor) {
                                          setFieldValue(
                                            "iconColor",
                                            s.iconColor
                                          );
                                          setColor(s.iconColor);
                                        }
                                      }}
                                      sx={{ cursor: "pointer" }}
                                    />
                                  )}
                                  <Icons.RemoveCircle
                                    onClick={() => {
                                      if (s.id > 0) {
                                        setDeleteMode(s);
                                      } else {
                                        handleRemove(s);
                                      }
                                    }}
                                    sx={{ cursor: "pointer" }}
                                  />
                                </Grid>
                              </Grid>
                            </StatusSortableItem>
                          ))}
                        </SortableContext>
                      </Grid>
                    </ul>
                  </DndContext>
                </Grid>
              </>
            ) : (
              <Grid size={12}>
                <Typography
                  textAlign={"center"}
                >{`Are you sure you want to delete the status ${deleteMode.name}? This will remove all jobs associated with it.`}</Typography>
                <Grid
                  container
                  direction={"row"}
                  justifyContent={"space-between"}
                >
                  <Base.Button
                    text="Cancel"
                    type="secondary"
                    onClick={() => {
                      setDeleteMode(undefined);
                    }}
                  />

                  <Base.Button
                    text="Delete"
                    onClick={() => {
                      handleRemove(deleteMode);
                      setDeleteMode(undefined);
                    }}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default ManageJobTypeStatusForm;
